import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../../PageHeader/PageHeader';
import { HitApi } from '../../Api/ApiHit';
import { searchBill, deleteBill } from '../../constant/Constant';
import { useDispatch, useSelector } from 'react-redux';
import { setApiJson } from '../../redux/Actions/ApiAction';
import AppTable from '../../components/AppTable/AppTable';
import { setPagination } from '../../redux/Actions/TableAction';
import toast from 'react-hot-toast';
import TableSearch from '../../components/TableSearch/TableSearch';
import DateSearch from '../../components/DateSearch/DateSearch';
import { exportToCSV, exportToPDF } from '../../utils/exportUtils';
import { maskPhoneNumber } from '../../utils/formatUtils';
import EditButton from '../../components/Edit/EditButton';
import DeleteButton from '../../components/Delete/DeleteButton';
import { hasPermission } from '../../utils/permissionUtils';

function AutoBillingMaster() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const ApiReducer = useSelector((state) => state.ApiReducer);
    const TableDataReducer = useSelector((state) => state.TableReducer);

    // Search state variables
    const [optionSelect, setOptionSelect] = useState('');
    const [valueSearched, setValueSearched] = useState('');
    const [isSearching, setIsSearching] = useState(false);
    const [activeDateRange, setActiveDateRange] = useState(null);
    const [activeTextSearch, setActiveTextSearch] = useState(null);

    // Permission checks
    const canEditBills = hasPermission('autoBilling', 'write');
    const canReadBills = hasPermission('autoBilling', 'read');
    const canDeleteBills = hasPermission('autoBilling', 'delete');
    const canAddBills = hasPermission('autoBilling', 'write');
    const hasAnyPermission = canEditBills || canDeleteBills || canReadBills;

    console.log({ canEditBills, canReadBills, canDeleteBills, canAddBills });

    useEffect(() => {
        if (canReadBills) {
            if (activeDateRange) {
                performDateRangeSearch(activeDateRange.startDate, activeDateRange.endDate);
            }
            else if (activeTextSearch) {
                fetchBills(activeTextSearch.key, activeTextSearch.value);
            }
            else {
                fetchBills();
            }
        }
    }, [TableDataReducer.pagination.page, TableDataReducer.pagination.limit, canReadBills]);

    const fetchBills = (searchKey, searchValue) => {
        setIsSearching(true);

        const searchObj = {
            status: 'pending'
        };

        if (searchKey && searchValue) {
            searchObj[searchKey] = searchValue;
        }
        const json = {
            page: TableDataReducer.pagination.page,
            limit: TableDataReducer.pagination.limit,
            search: searchObj
        };

        HitApi(json, searchBill).then((res) => {
            if (res?.statusCode === 200) {
                const billData = Array.isArray(res?.data?.docs) ? res?.data?.docs : [];
                dispatch(setApiJson(billData));

                const paginationData = {
                    total: res?.data?.totalDocs || 0,
                    page: TableDataReducer.pagination.page,
                    limit: TableDataReducer.pagination.limit
                };

                dispatch(setPagination(paginationData));
            } else {
                dispatch(setApiJson([]));
                toast.error('Failed to fetch bills. Please try again.');
            }
        }).catch(() => {
            dispatch(setApiJson([]));
            toast.error('Error fetching bills. Please try again.');
        }).finally(() => {
            setIsSearching(false);
        });
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    const formatTime = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: true
        });
    };

    const formatCurrency = (amount) => {
        if (amount === null || amount === undefined) return '₹0';
        return `₹${Number(amount).toFixed(2)}`;
    };

    const renderPaymentStatus = (payment) => {
        const status = payment?.paymentStatus || 'Pending';
        let bgColor = 'bg-yellow-100';
        let textColor = 'text-yellow-800';

        if (status === 'Paid') {
            bgColor = 'bg-green-100';
            textColor = 'text-green-800';
        } else if (status === 'Partial') {
            bgColor = 'bg-orange-100';
            textColor = 'text-orange-800';
        } else if (status === 'Unpaid') {
            bgColor = 'bg-red-100';
            textColor = 'text-red-800';
        }

        return (
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${bgColor} ${textColor}`}>
                {status}
            </span>
        );
    };

    const renderBillStatus = (status) => {
        const statusText = status || 'pending';
        let bgColor = 'bg-gray-100';
        let textColor = 'text-gray-800';

        if (statusText === 'completed') {
            bgColor = 'bg-green-100';
            textColor = 'text-green-800';
        } else if (statusText === 'cancelled') {
            bgColor = 'bg-red-100';
            textColor = 'text-red-800';
        }

        return (
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${bgColor} ${textColor}`}>
                {statusText.charAt(0).toUpperCase() + statusText.slice(1)}
            </span>
        );
    };

    const renderPaymentMethods = (payment) => {
        if (!payment?.activePaymentMethods || payment.activePaymentMethods.length === 0) {
            return <span className="text-xs text-gray-500">No payment</span>;
        }

        return (
            <div className="space-y-1">
                {payment.activePaymentMethods.map((method, index) => (
                    <div key={index} className="flex items-center text-xs">
                        <span className="font-medium text-gray-700">{method.method}:</span>
                        <span className="ml-1 text-gray-900">{formatCurrency(method.amount)}</span>
                    </div>
                ))}
            </div>
        );
    };

    // Search functionality
    const handleSearch = () => {
        if (!optionSelect) {
            toast.error('Please select a search option');
            return;
        }
        if (!valueSearched || valueSearched.trim() === '') {
            toast.error('Please enter a search value');
            return;
        }

        // Store active text search state
        setActiveTextSearch({ key: optionSelect, value: valueSearched.trim() });
        // Clear date range state
        setActiveDateRange(null);

        fetchBills(optionSelect, valueSearched.trim());
    };

    // Search options based on bill fields
    const options = [
        { label: 'Bill Number', value: 'billNumber' },
        { label: 'Transaction ID', value: 'transactionId' },
        { label: 'Client Name', value: 'client.name' },
        { label: 'Phone Number', value: 'client.phoneNumber' },
        { label: 'Payment Status', value: 'payment.paymentStatus' },
        { label: 'Bill Status', value: 'status' },
    ];

    const handleClearSearch = () => {
        setOptionSelect('');
        setValueSearched('');
        setActiveTextSearch(null);
        setActiveDateRange(null);
        fetchBills();
    };

    // Perform date range search (without setting state)
    const performDateRangeSearch = (startDate, endDate) => {
        setIsSearching(true);

        const startTimestamp = new Date(startDate).setHours(0, 0, 0, 0); // Start of day
        const endTimestamp = new Date(endDate).setHours(23, 59, 59, 999); // End of day

        const searchObj = {
            status: 'pending', // Only fetch pending bills
            createdAt: {
                $gte: startTimestamp,
                $lte: endTimestamp
            }
        };

        const json = {
            page: TableDataReducer.pagination.page,
            limit: TableDataReducer.pagination.limit,
            search: searchObj
        };

        HitApi(json, searchBill).then((res) => {
            if (res?.statusCode === 200) {
                const billData = Array.isArray(res?.data?.docs) ? res?.data?.docs : [];
                dispatch(setApiJson(billData));
                const paginationData = {
                    total: res?.data?.totalDocs || 0,
                    page: TableDataReducer.pagination.page,
                    limit: TableDataReducer.pagination.limit
                };

                dispatch(setPagination(paginationData));
                toast.success(`Found ${res?.data?.totalDocs || 0} pending bills in the selected date range`);
            } else {
                dispatch(setApiJson([]));
                toast.error('Failed to fetch bills. Please try again.');
            }
        }).catch(() => {
            dispatch(setApiJson([]));
            toast.error('Error fetching bills. Please try again.');
        }).finally(() => {
            setIsSearching(false);
        });
    };

    const handleDateRangeSearch = (startDate, endDate) => {
        setActiveDateRange({ startDate, endDate });
        // Clear text search state
        setActiveTextSearch(null);

        performDateRangeSearch(startDate, endDate);
    };

    const handleEditBill = (billData) => {
        if (!canEditBills) {
            toast.error('You do not have permission to edit bills');
            return;
        }
        navigate(`/autoBilling/edit/${billData._id}`, { state: { billData } });
    };

    const handleDeleteBill = async (billData) => {
        if (!canDeleteBills) {
            toast.error('You do not have permission to delete bills');
            return;
        }

        const isConfirmed = window.confirm(`Are you sure you want to delete bill ${billData.billNumber || 'this bill'}?`);

        if (!isConfirmed) {
            return;
        }

        try {
            toast.loading('Deleting bill...', { id: 'delete-bill' });

            const json = {
                _id: billData._id
            };

            const response = await HitApi(json, deleteBill);

            if (response?.statusCode === 200 || response?.statusCode === 201) {
                toast.success('Bill deleted successfully!', {
                    id: 'delete-bill',
                    duration: 3000
                });

                // Refresh the bills list
                if (activeDateRange) {
                    performDateRangeSearch(activeDateRange.startDate, activeDateRange.endDate);
                } else if (activeTextSearch) {
                    fetchBills(activeTextSearch.key, activeTextSearch.value);
                } else {
                    fetchBills();
                }
            } else {
                toast.error(response?.message || 'Failed to delete bill', {
                    id: 'delete-bill',
                    duration: 4000
                });
            }
        } catch (error) {
            console.error('Error deleting bill:', error);
            toast.error('Network error. Please try again.', {
                id: 'delete-bill',
                duration: 4000
            });
        }
    };

    const baseIndex = (TableDataReducer.pagination.page - 1) * TableDataReducer.pagination.limit;

    const baseTableHeaders = [
        {
            title: "S.No",
            key: "serialNumber",
            width: "60px",
            align: "center",
            render: (_, __, index) => {
                const serialNumber = baseIndex + index + 1;
                return (
                    <div className="text-center">
                        <span className="text-sm font-medium text-gray-900">{serialNumber}</span>
                    </div>
                );
            }
        },
        {
            title: "Bill Details",
            key: "billNumber",
            render: (value, row) => (
                <div>
                    <div className="font-medium text-gray-900 text-sm">{value || 'N/A'}</div>
                    <div className="text-xs text-gray-500">
                        {row.transactionId || 'No transaction ID'}
                    </div>
                    <div className="text-xs text-gray-400 mt-1">
                        {formatDate(row.timestamp)}
                    </div>
                </div>
            )
        },
        {
            title: "Client Details",
            key: "client",
            render: (client) => (
                <div>
                    <div className="font-medium text-gray-900 text-sm">
                        {client?.name || 'N/A'}
                    </div>
                    <div className="text-xs text-gray-500">
                        {maskPhoneNumber(client?.phoneNumber) || 'No phone'}
                    </div>
                    <div className="text-xs text-gray-400">
                        {client?.gender || 'N/A'}
                    </div>
                </div>
            )
        },

        {
            title: "Items",
            key: "items",
            align: "start",
            render: (_, row) => (
                <div className="text-start">
                    {row.services && row.services.length > 0 && (
                        <div className="text-xs">
                            <span className="font-medium text-blue-600">Services: </span>
                            <span className="text-gray-900">{row.services.length}</span>
                        </div>
                    )}
                    {row.products && row.products.length > 0 && (
                        <div className="text-xs">
                            <span className="font-medium text-purple-600">Products: </span>
                            <span className="text-gray-900">{row.products.length}</span>
                        </div>
                    )}
                    {row.newMemberships && row.newMemberships.length > 0 && (
                        <div className="text-xs">
                            <span className="font-medium text-green-600">Memberships: </span>
                            <span className="text-gray-900">{row.newMemberships.length}</span>
                        </div>
                    )}
                    {(!row.services?.length && !row.products?.length && !row.newMemberships?.length) && (
                        <span className="text-xs text-gray-400">No items</span>
                    )}
                </div>
            )
        },
        {
            title: "Amount Details",
            key: "calculations",
            render: (calculations) => {
                const grandTotal = calculations?.totals?.grandTotal || 0;
                const totalDiscount = calculations?.totals?.totalDiscount || 0;
                const originalPrice = grandTotal + totalDiscount;

                return (
                    <div className="space-y-1 text-xs">
                        {totalDiscount > 0 ? (
                            <>
                                <div className="flex justify-between items-center">
                                    <span className="text-gray-600">Original Price:</span>
                                    <span className="font-medium text-gray-900">{formatCurrency(originalPrice)}</span>
                                </div>
                                <div className="flex justify-between items-center">
                                    <span className="text-green-600">Discount (-):</span>
                                    <span className="font-medium text-green-600">-{formatCurrency(totalDiscount)}</span>
                                </div>
                                <div className="flex justify-between items-center pt-1 border-t border-gray-200">
                                    <span className="text-gray-900 font-semibold">Paid Amount:</span>
                                    <span className="font-bold text-gray-900">{formatCurrency(grandTotal)}</span>
                                </div>
                            </>
                        ) : (
                            <div className="flex justify-between items-center">
                                <span className="text-gray-900 font-semibold">Amount To Pay:</span>
                                <span className="font-bold text-gray-900">{formatCurrency(calculations?.totals?.finalAmount)}</span>
                            </div>
                        )}
                        {calculations?.totals?.gstAmount > 0 && (
                            <div className="flex justify-between items-center text-gray-500 pt-1">
                                <span>GST:</span>
                                <span>{formatCurrency(calculations?.totals?.gstAmount)}</span>
                            </div>
                        )}
                    </div>
                );
            }
        },
        {
            title: "Payment",
            key: "payment",
            render: (payment) => (
                <div className="space-y-2">
                    {renderPaymentStatus(payment)}
                    <div className="mt-1">
                        {renderPaymentMethods(payment)}
                    </div>
                    {payment?.totalPaid > 0 && (
                        <div className="text-xs font-medium text-gray-700">
                            Paid: {formatCurrency(payment.totalPaid)}
                        </div>
                    )}
                </div>
            )
        },
        {
            title: "Status",
            key: "status",
            align: "start",
            render: (value) => (
                <div className="text-start">
                    {renderBillStatus(value)}
                </div>
            )
        },
        {
            title: "Coupon",
            key: "appliedCoupon",
            align: "start",
            render: (coupon) => {
                if (!coupon || !coupon.code) {
                    return (
                        <span className="text-xs text-gray-400">No coupon</span>
                    );
                }
                return (
                    <div className="text-start">
                        <div className=" text-purple-800 rounded text-xs font-medium">
                            {coupon.code}
                        </div>
                        {coupon.discountAmount > 0 && (
                            <div className="text-xs text-green-600 mt-1">
                                -{formatCurrency(coupon.discountAmount)}
                            </div>
                        )}
                    </div>
                );
            }
        },
        {
            title: "Time",
            key: "timestamp",
            align: "start",
            render: (value) => (
                <div className="text-start text-xs text-gray-600">
                    {formatTime(value)}
                </div>
            )
        }
    ];

    // Add Actions column only if user has any permission
    const tableHeaders = [
        ...baseTableHeaders,
        {
            title: "Action",
            key: "action",
            align: "center",
            width: "120px",
            render: (_, row) => {
                if (!hasAnyPermission) {
                    return (
                        <div className="flex justify-center">
                            <span className="text-sm text-gray-500">Loading...</span>
                        </div>
                    );
                }

                return (
                    <div className="flex justify-center gap-2">
                        {canEditBills && (
                            <EditButton
                                onEdit={() => handleEditBill(row)}
                                itemName="bill"
                                tooltip="Edit Bill"
                                disabled={isSearching}
                            />
                        )}
                        {canDeleteBills && (
                            <DeleteButton
                                onDelete={() => handleDeleteBill(row)}
                                itemName="bill"
                                disabled={isSearching}
                            />
                        )}
                    </div>
                );
            }
        }
    ];


    const handleExportData = async (format) => {
        try {
            toast.loading('Preparing export...');

            // Determine which search params to use
            let searchObj = {
                status: 'pending' // Only export pending bills
            };

            if (activeDateRange) {
                const startTimestamp = new Date(activeDateRange.startDate).setHours(0, 0, 0, 0);
                const endTimestamp = new Date(activeDateRange.endDate).setHours(23, 59, 59, 999);
                searchObj.createdAt = {
                    $gte: startTimestamp,
                    $lte: endTimestamp
                };
            } else if (activeTextSearch) {
                searchObj[activeTextSearch.key] = activeTextSearch.value;
            }

            // Fetch all data without pagination
            const json = {
                page: 1,
                limit: 10000, // Large number to get all data
                search: searchObj
            };

            const res = await HitApi(json, searchBill);

            if (res?.statusCode === 200 && res?.data?.docs) {
                const allData = res.data.docs;

                if (allData.length === 0) {
                    toast.dismiss();
                    toast.error('No data to export');
                    return;
                }


                const exportHeaders = [
                    { title: 'S.No', key: 'serialNumber' },
                    { title: 'Bill Number', key: 'billNumber' },
                    { title: 'Transaction ID', key: 'transactionId' },
                    { title: 'Date', key: 'date' },
                    { title: 'Client Name', key: 'clientName' },
                    { title: 'Phone Number', key: 'phoneNumber' },
                    { title: 'Gender', key: 'gender' },
                    { title: 'Bill Type', key: 'billType' },
                    { title: 'Services Count', key: 'servicesCount' },
                    { title: 'Products Count', key: 'productsCount' },
                    { title: 'Memberships Count', key: 'membershipsCount' },
                    { title: 'Original Price', key: 'originalPrice' },
                    { title: 'Discount', key: 'discount' },
                    { title: 'Final Amount', key: 'finalAmount' },
                    { title: 'GST Amount', key: 'gstAmount' },
                    { title: 'Payment Status', key: 'paymentStatus' },
                    { title: 'Payment Methods', key: 'paymentMethods' },
                    { title: 'Total Paid', key: 'totalPaid' },
                    { title: 'Bill Status', key: 'status' },
                    { title: 'Coupon Code', key: 'couponCode' },
                    { title: 'Coupon Discount', key: 'couponDiscount' },
                    { title: 'Time', key: 'time' }
                ];

                // Flatten and format data for export
                const flattenedData = allData.map((row, index) => {
                    const grandTotal = row.calculations?.totals?.grandTotal || 0;
                    const totalDiscount = row.calculations?.totals?.totalDiscount || 0;
                    const originalPrice = grandTotal + totalDiscount;

                    // Payment methods as string
                    let paymentMethodsStr = 'No payment';
                    if (row.payment?.activePaymentMethods && row.payment.activePaymentMethods.length > 0) {
                        paymentMethodsStr = row.payment.activePaymentMethods
                            .map(method => `${method.method}: ${Number(method.amount).toFixed(2)}`)
                            .join(', ');
                    }

                    return {
                        serialNumber: index + 1,
                        billNumber: row.billNumber || 'N/A',
                        transactionId: row.transactionId || 'N/A',
                        date: formatDate(row.timestamp),
                        clientName: row.client?.name || 'N/A',
                        phoneNumber: maskPhoneNumber(row.client?.phoneNumber) || 'N/A',
                        gender: row.client?.gender || 'N/A',
                        billType: row.billType ? row.billType.replace(/_/g, ' ') : 'N/A',
                        servicesCount: row.services?.length || 0,
                        productsCount: row.products?.length || 0,
                        membershipsCount: row.newMemberships?.length || 0,
                        originalPrice: Number(originalPrice).toFixed(2),
                        discount: Number(totalDiscount).toFixed(2),
                        finalAmount: Number(grandTotal).toFixed(2),
                        gstAmount: Number(row.calculations?.totals?.gstAmount || 0).toFixed(2),
                        paymentStatus: row.payment?.paymentStatus || 'Pending',
                        paymentMethods: paymentMethodsStr,
                        totalPaid: Number(row.payment?.totalPaid || 0).toFixed(2),
                        status: row.status ? row.status.charAt(0).toUpperCase() + row.status.slice(1) : 'Pending',
                        couponCode: row.appliedCoupon?.code || 'No coupon',
                        couponDiscount: Number(row.appliedCoupon?.discountAmount || 0).toFixed(2),
                        time: formatTime(row.timestamp)
                    };
                });

                const filename = `auto_bills_export_${new Date().toISOString().split('T')[0]}`;

                toast.dismiss();

                if (format === 'csv') {
                    exportToCSV(flattenedData, exportHeaders, `${filename}.csv`);
                    toast.success(`Exported ${allData.length} bills to CSV`);
                } else if (format === 'pdf') {
                    exportToPDF(flattenedData, exportHeaders, `${filename}.pdf`, 'Auto Bills Report');
                    toast.success(`Exported ${allData.length} bills to PDF`);
                }
            } else {
                toast.dismiss();
                toast.error('Failed to fetch data for export');
            }
        } catch (error) {
            toast.dismiss();
            toast.error('Error exporting data');
            console.error('Export error:', error);
        }
    };

    const billsData = Array.isArray(ApiReducer.apiJson) ? ApiReducer.apiJson : [];

    return (
        <div className="p-5">
            <PageHeader
                title={'Auto Billing Management'}
                description={'View and manage auto-generated bills'}
            />

            {!hasAnyPermission ? (
                <div className="flex justify-center items-center h-64">
                    <div className="text-lg">Loading permissions...</div>
                </div>
            ) : (
                <AppTable
                    TH={tableHeaders}
                    TD={billsData}
                    isLoading={isSearching}
                    emptyMessage="No bills found"
                    enableExport={true}
                    onExportData={handleExportData}
                    exportFileName="auto_bills_export"
                    // buttonText={canAddBills && "Add Bill"}
                    // navigateTo={canAddBills && "/autoBilling/add"}
                    TableSearch={
                        <TableSearch
                            options={options}
                            onClick={handleSearch}
                            optionSelect={optionSelect}
                            setOptionSelect={setOptionSelect}
                            setValueSearched={setValueSearched}
                            valueSearched={valueSearched}
                            onClear={handleClearSearch}
                        >
                            <DateSearch
                                onSearch={handleDateRangeSearch}
                                onClear={handleClearSearch}
                            />
                        </TableSearch>
                    }
                />
            )}
        </div>
    );
}

export default AutoBillingMaster;